import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popup from "reactjs-popup";
import { toast } from 'react-toastify';
import useIsMobile from '../../shared/hooks/useIsMobile';
import V3OfferTemplateDetailsModal from './V3OfferTemplateDetailsModal';
import TemplateConfirmDialog from './TemplateConfirmDialog';
import templateErrorMessage from '../utils/templateErrorMessage';
import TemplateMenuItem from './TemplateMenuItem';

const axios = require('axios').default;

const dropdownContentStyle = {
  textAlign: "left",
  border: "1px solid var(--bk-c-e9eaeb, #E9EAEB)",
  borderRadius: "8px",
  boxShadow: "0px 12px 16px -4px rgba(10,13,18,0.08), " +
             "0px 4px 6px -2px rgba(10,13,18,0.03), " +
             "0px 2px 2px -1px rgba(10,13,18,0.04)",
  width: "280px",
  maxWidth: "calc(100vw - 24px)",
  padding: "0px"
};

const modalContentStyle = {
  border: '1px solid var(--sl-border-secondary)',
  background: 'var(--sl-bg-primary)',
  color: 'var(--sl-text-primary)',
  borderRadius: '8px',
  boxShadow: '0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)',
  width: '320px',
  padding: '0'
};

// O-5024: the save returns 200 even when only the OFFER half was written —
// which happens when the budget half is a template the booker paired by hand in
// Settings, and rewriting it from one show would redefine it for every other
// event template pointing at it. Saying "saved successfully" there is how their
// budget edits come to be silently reverted by the next apply.
const savedMessage = (data, success) => (
  data?.budget_half_skipped
    ? 'Deal terms saved. The budget half is a template shared from Settings, so it was left as it is.'
    : success
);

const toastOptions = {
  position: toast.POSITION.TOP_CENTER,
  draggable: false,
  closeOnClick: false,
  autoClose: 3000,
  hideProgressBar: true
};

// O-5024: saves the show as an event template — the primary offer's deal terms
// as the offer template, the shared budget's line items as the budget template.
// Also carries View details and Unlink, which the offer section's own template
// menu no longer shows on the primary offer.
const SaveEventTemplateDropdown = ({
  team,
  csrfToken,
  budget,
  currentTemplateName,
  offerTemplateId,
  onTemplateCreated,
  onTemplateSaved,
  onBudgetPatched
}) => {
  const [templateName, setTemplateName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // 'save' | 'unlink' | null — both overwrite or drop shared state, so both ask.
  const [pendingAction, setPendingAction] = useState(null);
  const isMobile = useIsMobile();

  const buttonStyle = {
    background: 'var(--bk-c-ffffff, #ffffff)',
    border: '1px solid var(--bk-c-d5d7da, #d5d7da)',
    borderRadius: '8px',
    height: '40px',
    padding: '0 14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--bk-c-414651, #414651)',
    boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
    whiteSpace: 'nowrap'
  };

  const buttonFocusedStyle = {
    ...buttonStyle,
    boxShadow: '0px 0px 0px 2px white, 0px 0px 0px 4px var(--bk-c-98c2db, #98C2DB)'
  };

  const menuItemsContainerStyle = {
    padding: '4px 0'
  };

  const menuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    cursor: 'pointer',
    borderRadius: '6px',
    margin: '1px 6px',
    fontFamily: "'Open Sans', sans-serif",
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '20px',
    color: 'var(--bk-c-414651, #414651)',
    textDecoration: 'none'
  };

  const dividerStyle = {
    height: '1px',
    backgroundColor: 'var(--bk-c-e9eaeb, #E9EAEB)',
    margin: '4px 0'
  };

  const iconStyle = {
    width: '16px',
    fontSize: '14px',
    color: 'var(--bk-c-717680, #717680)'
  };

  const templateLinked = !!budget.event_template_id;
  const hasCurrentTemplate = templateLinked && currentTemplateName;

  const errorToast = (fallback, error) => {
    toast.error(
      templateErrorMessage(error, fallback),
      { ...toastOptions, autoClose: 5000 }
    );
  };

  const handleSaveAsNewTemplate = async (closeModal) => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name', { ...toastOptions, autoClose: 5000 });
      return;
    }

    setIsSaving(true);

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      // Server-authored: it captures the offer template and the budget template
      // off this budget and its primary offer. Always fresh children, so saving
      // a new template while another one is applied can't write through to it.
      const response = await axios.post(
        `/teams/${team.id}/event_templates`,
        { from_v3_budget_id: budget.id, event_template: { name: templateName.trim() } }
      );

      toast.success(savedMessage(response.data, 'Template saved successfully'), toastOptions);

      setTemplateName('');
      closeModal();

      // The server persists the link; mirror just that one field so the control
      // flips to "Save to Current Template" without a reload. Merging a whole
      // budget object here would push the per-offer-narrowed derivative back
      // over the shared budget state and drop the siblings' rows with it.
      if (onBudgetPatched) onBudgetPatched({ event_template_id: response.data.id });

      if (onTemplateCreated) onTemplateCreated(response.data);
    } catch (error) {
      errorToast('Failed to save template', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveToCurrentTemplate = async () => {
    if (!budget.event_template_id) return;

    setIsSaving(true);

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const response = await axios.patch(
        `/teams/${team.id}/event_templates/${budget.event_template_id}`,
        { from_v3_budget_id: budget.id }
      );

      toast.success(savedMessage(response.data, 'Template updated successfully'), toastOptions);
      setPendingAction(null);
      // A template that had no offer template gains one on this save; without
      // this the menu's "View template details" stays hidden until reload.
      if (onTemplateSaved) onTemplateSaved(response.data);
    } catch (error) {
      errorToast('Failed to update template', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnlink = async () => {
    if (!budget.event_template_id) return;

    setIsSaving(true);

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(
        `/teams/${team.id}/budgets/v3/${budget.id}/unlink_event_template`
      );

      toast.success('Unlinked from template', toastOptions);
      setPendingAction(null);

      if (onBudgetPatched) onBudgetPatched({ event_template_id: null });
    } catch (error) {
      errorToast('Failed to unlink template', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Popup
        trigger={open => (
          <button
            type="button"
            style={open ? buttonFocusedStyle : buttonStyle}
            title="Event template options"
            data-testid="save-event-template-dropdown"
          >
            <i className="far fa-save" style={{ fontSize: '16px', color: 'var(--bk-c-414651, #414651)' }}></i>
            <span style={{ padding: '0 2px' }}>
              {hasCurrentTemplate ? 'Save to Current Template' : 'Save as Template'}
            </span>
            <i
              className="fas fa-chevron-down"
              style={{ fontSize: '12px', color: 'var(--bk-c-414651, #414651)' }}
            ></i>
          </button>
        )}
        position={isMobile ? "bottom left" : "bottom right"}
        on="click"
        closeOnDocumentClick
        contentStyle={dropdownContentStyle}
        className="save-event-template-dropdown"
        arrow={false}
        offsetY={4}
      >
        {close => (
          <div>
            <div style={menuItemsContainerStyle} role="menu">
              {hasCurrentTemplate && (
                <>
                  <TemplateMenuItem
                    icon="fas fa-save"
                    label="Save to current template"
                    subtitle={currentTemplateName}
                    onClick={() => { close(); setPendingAction('save'); }}
                    disabled={isSaving}
                    testId="save-event-template-current"
                  />
                  <div style={dividerStyle} role="separator"></div>
                </>
              )}
              <Popup
                trigger={
                  // A <button>, not an <a href="#">: reactjs-popup clones the
                  // trigger and replaces its onClick with the open handler, so a
                  // preventDefault of ours never runs and the anchor navigates,
                  // jumping the page to the top.
                  <button
                    type="button"
                    role="menuitem"
                    style={{
                      ...menuItemStyle,
                      width: 'calc(100% - 12px)',
                      background: 'none',
                      border: 0,
                      textAlign: 'left',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '2px'
                    }}
                    data-testid="save-event-template-new"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="fas fa-plus" style={iconStyle}></i>
                      <span>Save as new template</span>
                    </div>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: '400',
                      color: 'var(--bk-c-717680, #717680)',
                      marginLeft: '24px'
                    }}>
                      Create a new template from this show
                    </span>
                  </button>
                }
                modal
                closeOnDocumentClick={false}
                onOpen={() => setTemplateName('')}
                contentStyle={modalContentStyle}
                onClose={() => close()}
              >
                {closeModal => (
                  <div style={{ padding: '20px' }}>
                    <h5 style={{
                      marginBottom: '16px',
                      fontWeight: '600',
                      fontSize: '16px'
                    }}>
                      Save as Template
                    </h5>
                    <div style={{ marginBottom: '16px' }}>
                      <input
                        type="text"
                        className="form-control"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="Template name"
                        autoFocus
                        style={{ fontSize: '14px' }}
                      />
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      justifyContent: 'flex-end'
                    }}>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        style={{
                          borderRadius: '6px',
                          background: 'var(--sl-bg-primary)',
                          color: 'var(--sl-text-secondary)',
                          borderColor: 'var(--sl-border-primary)'
                        }}
                        onClick={() => {
                          setTemplateName('');
                          closeModal();
                        }}
                        disabled={isSaving}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ borderRadius: '6px' }}
                        onClick={() => handleSaveAsNewTemplate(closeModal)}
                        disabled={isSaving || !templateName.trim()}
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}
              </Popup>

              {templateLinked && offerTemplateId && (
                <TemplateMenuItem
                  icon="far fa-eye"
                  label="View template details"
                  onClick={() => { close(); setDetailsOpen(true); }}
                  testId="save-event-template-details"
                />
              )}

              {templateLinked && (
                <>
                  <div style={dividerStyle} role="separator"></div>
                  <TemplateMenuItem
                    icon="far fa-unlink"
                    label="Unlink from template"
                    onClick={() => { close(); setPendingAction('unlink'); }}
                    disabled={isSaving}
                    destructive
                    testId="save-event-template-unlink"
                  />
                </>
              )}
            </div>
          </div>
        )}
      </Popup>

      <TemplateConfirmDialog
        open={pendingAction === 'save'}
        title={`Save this show to “${currentTemplateName}”?`}
        body={
          'This overwrites the template’s deal terms and budget with this show’s, '
          + 'for everyone who applies it later. It does not change this show.'
        }
        confirmLabel="Save to template"
        busyLabel="Saving..."
        busy={isSaving}
        onConfirm={handleSaveToCurrentTemplate}
        onCancel={() => setPendingAction(null)}
        testId="save-event-template-confirm"
      />

      <TemplateConfirmDialog
        open={pendingAction === 'unlink'}
        title="Unlink this show from its event template?"
        body="Your offer and budget stay exactly as they are — only the link is removed."
        confirmLabel="Unlink"
        busyLabel="Unlinking..."
        busy={isSaving}
        destructive
        onConfirm={handleUnlink}
        onCancel={() => setPendingAction(null)}
        testId="unlink-event-template-confirm"
      />

      <V3OfferTemplateDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        team={team}
        templateId={offerTemplateId}
        isBlueprint={false}
        csrfToken={csrfToken}
      />
    </>
  );
};

SaveEventTemplateDropdown.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  budget: PropTypes.object.isRequired,
  currentTemplateName: PropTypes.string,
  offerTemplateId: PropTypes.number,
  onTemplateCreated: PropTypes.func,
  onTemplateSaved: PropTypes.func,
  onBudgetPatched: PropTypes.func
};

SaveEventTemplateDropdown.defaultProps = {
  currentTemplateName: null,
  offerTemplateId: null,
  onTemplateCreated: null,
  onTemplateSaved: null,
  onBudgetPatched: null
};

export default SaveEventTemplateDropdown;
